import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import WaitlistForm from "./WaitlistForm";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const verticals = [
  {
    emoji: "✈️",
    title: "Flex Travel",
    badge: "Live Now",
    badgeType: "live" as const,
    description:
      "Flights, hotels and packages worldwide. Pay monthly from AED 500.",
    ctaLabel: "Start Planning",
    route: "/travel",
    active: true,
  },
  {
    emoji: "🎓",
    title: "Flex Education",
    badge: "Coming Soon",
    badgeType: "coming" as const,
    description:
      "School fees, tuition and registration. Split into manageable monthly payments.",
    ctaLabel: "Join Waitlist",
    route: null,
    active: false,
  },
  {
    emoji: "🏥",
    title: "Flex Healthcare",
    badge: "Coming Soon",
    badgeType: "coming" as const,
    description:
      "Elective and essential procedures. Finance the gap your insurance doesn't cover.",
    ctaLabel: "Join Waitlist",
    route: null,
    active: false,
  },
];

const VerticalCardsSection = () => {
  const navigate = useNavigate();
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistSource, setWaitlistSource] = useState("education");

  const openWaitlist = (source: string) => {
    setWaitlistSource(source);
    setWaitlistOpen(true);
  };

  return (
    <>
      <section className="py-24 px-4 bg-background">
        <div className="max-w-5xl mx-auto">
          {/* HEADER */}
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-semibold text-primary mb-4">
              Built for Life's Big Moments
            </h2>
            <p className="text-lg text-muted-foreground font-body max-w-2xl mx-auto leading-relaxed">
              Whatever matters most to you, Flexaura makes it affordable with flexible monthly payments from AED 2,000 to AED 200,000+
            </p>
            <p className="text-sm text-muted-foreground font-body mt-3">
              Eligibility and limits are determined by our partners.
            </p>
          </div>

          {/* CARDS GRID */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {verticals.map((vertical, index) => (
              <div
                key={index}
                className={`
                  relative flex flex-col rounded-2xl border border-border
                  overflow-hidden transition-shadow duration-300
                  bg-card
                  ${vertical.active ? "hover:shadow-lg hover:shadow-primary/5" : "opacity-70"}
                `}
              >
                <div className="p-8 flex flex-col flex-grow">
                  {/* Emoji icon */}
                  <div className="w-14 h-14 rounded-xl bg-primary/10 border border-border flex items-center justify-center mb-6 text-2xl">
                    {vertical.emoji}
                  </div>

                  {/* Title + Badge */}
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-xl font-heading font-semibold text-foreground">
                      {vertical.title}
                    </h3>
                    {vertical.badgeType === "live" ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full" style={{ color: "#C9A84C", backgroundColor: "rgba(201,168,76,0.12)" }}>
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        {vertical.badge}
                      </span>
                    ) : (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                        {vertical.badge}
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-muted-foreground font-body text-[15px] leading-relaxed mb-8 flex-grow">
                    {vertical.description}
                  </p>

                  {/* CTA */}
                  {vertical.active ? (
                    <Button
                      size="sm"
                      className="w-full font-medium"
                      onClick={() => navigate(vertical.route!)}
                    >
                      {vertical.ctaLabel}
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full border-primary/20 text-foreground hover:bg-primary/5 hover:border-primary/30 font-medium"
                      onClick={() => openWaitlist(vertical.title.toLowerCase().replace("flex ", ""))}
                    >
                      {vertical.ctaLabel}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Waitlist modal */}
      <Dialog open={waitlistOpen} onOpenChange={setWaitlistOpen}>
        <DialogContent className="sm:max-w-md">
          <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
            Join the Waitlist
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Be the first to know when Flex {waitlistSource.charAt(0).toUpperCase() + waitlistSource.slice(1)} launches.
          </p>
          <WaitlistForm
            source={`vertical-card-${waitlistSource}`}
            buttonText="Notify Me"
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VerticalCardsSection;
